import { Deployment, Project, ProjectStatus } from '../../../types';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';

import DefaultLayout from '@/components/nav/DefaultLayout';
import LoadingState from '../../components/ui/loading-state';
import ProjectDetails from '../../components/projects/ProjectDetails';
import { projectService } from '../../services/ProjectService';

// Helper function to transform deployment status
const transformDeploymentStatus = (status: string): 'completed' | 'failed' | 'in_progress' => {
    if (status === 'completed') return 'completed';
    if (status === 'failed') return 'failed';
    return 'in_progress';
};

const ProjectDetailsPage = () => {
    const params = useParams({ from: '/org/$orgSlug/project-details/$projectId' });
    const navigate = useNavigate();
    const [project, setProject] = useState<Project | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        const fetchProjectDetails = async () => {
            if (!params.projectId) {
                navigate({ to: '/org/$orgSlug', params: { orgSlug: params.orgSlug } });
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const projectMetadata = await projectService.getProject(params.projectId);

                if (projectMetadata && isMounted) {
                    const deployments: Deployment[] = (projectMetadata.deployments || []).map(d => ({
                        projectSlug: d.projectSlug,
                        build_url: d.build_url || '',
                        createdAt: d.createdAt,
                        deployment_id: d.deployment_id,
                        githubUsername: d.githubUsername || '',
                        isActive: d.isActive || false,
                        message: d.message || '',
                        orgId: d.orgId || null,
                        service_url: d.service_url || '',
                        status: transformDeploymentStatus(d.status),
                        timestamp: d.timestamp || d.createdAt,
                        updatedAt: d.updatedAt || d.createdAt,
                        userId: d.userId || '',
                        custom_domain: d.custom_domain,
                    }));

                    const projectData: Project = {
                        id: projectMetadata.id,
                        name: projectMetadata.name,
                        status: projectMetadata.isPublished ? ProjectStatus.PUBLISHED : ProjectStatus.DRAFT,
                        repository: projectMetadata.repository,
                        framework: projectMetadata.framework,
                        createdAt: new Date(projectMetadata.createdAt).toISOString(),
                        updatedAt: projectMetadata.updatedAt
                            ? new Date(projectMetadata.updatedAt).toISOString()
                            : new Date(projectMetadata.createdAt).toISOString(),
                        deployments,
                        url: projectMetadata.url || `/project/${projectMetadata.id}`,
                        deploymentUrl: projectMetadata.deploymentUrl,
                        branch: projectMetadata.branch || 'main',
                        icon: projectMetadata.icon || '📄',
                        ownerName: projectMetadata.ownerName || projectMetadata.userId || 'You',
                        ownerImage: projectMetadata.ownerImage || '',
                        isPublished: projectMetadata.isPublished || false,
                        time: projectMetadata.updatedAt
                            ? new Date(projectMetadata.updatedAt).toISOString()
                            : new Date(projectMetadata.createdAt).toISOString(),
                        onDelete: async (id: string) => {
                            try {
                                await projectService.deleteProject(id);
                                navigate({ to: '/org/$orgSlug', params: { orgSlug: params.orgSlug } });
                            } catch (error) {
                                console.error('Error deleting project:', error);
                            }
                        },
                        isQuickStart: projectMetadata.isQuickStart || false,
                    };
                    setProject(projectData);
                } else if (isMounted) {
                    setError(`Project with ID ${params.projectId} not found.`);
                }
            } catch (err) {
                console.error('Error loading project details:', err);
                if (isMounted) {
                    setError(err instanceof Error ? err.message : "Failed to load project details.");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchProjectDetails();

        return () => {
            isMounted = false;
        };
    }, [params.projectId, params.orgSlug, navigate]);

    const handleClose = () => {
        navigate({ to: '/org/$orgSlug', params: { orgSlug: params.orgSlug } });
    };

    console.log("Rendering ProjectDetailsPage state:", { loading, project, error });

    if (loading) {
        return <div className="flex items-center justify-center h-screen">
            <LoadingState size="md" message="Loading project details..." />
        </div>;
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-screen p-4 text-center">
                <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Project</h2>
                <p className="text-red-500 mb-4">{error}</p>
                <button
                    onClick={handleClose}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                    Return to Projects
                </button>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="flex items-center justify-center h-screen">
                <p>Project not found.</p>
                <button
                    onClick={handleClose}
                    className="ml-4 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                    Return to Projects
                </button>
            </div>
        );
    }

    return (
        <DefaultLayout
            onNewProject={() => { }}
            onOpenProject={() => { }}
            onOpenPreview={() => { }}
        >
            <ProjectDetails project={project} onClose={handleClose} />
        </DefaultLayout>
    );
};

export default ProjectDetailsPage;
